import { useCallback } from 'react';
import { ConnectionHandler, type RecordSourceProxy, ConnectionInterface } from 'relay-runtime';
import type { IntlShape } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { FlagConfiguration } from '@atlassian/jira-flags';
import RelayDataID from '@atlassian/relay-data-id';
import { useConnectionsList } from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import {
	updateParentHasChildren,
	useUpdateConnectionDetails,
} from '../../controllers/issue-realtime-updater/utils.tsx';
import messages from './messages.tsx';

import {
	UPDATE_ITEM_PARENT_FAIL_400_FLAG,
	UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM,
	UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM_GALAXIA,
	UPDATE_ITEM_PARENT_FAIL_404_FLAG,
	UPDATE_ITEM_PARENT_FAIL_404_FLAG_WITH_ISSUE_TERM,
} from './constants.tsx';

export const getFlagDetails = (
	statusCode: number | null,
	intl: IntlShape,
	issueKey?: string | null,
): FlagConfiguration | null => {
	switch (statusCode) {
		case 400: {
			if (issueKey && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
				return getUpdateItemParentFail400FlagWithWorkType(intl, issueKey);
			}
			if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM_GALAXIA;
				}

				return UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM;
			}
			return UPDATE_ITEM_PARENT_FAIL_400_FLAG;
		}
		case 404: {
			if (issueKey && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
				return getUpdateItemParentFail404FlagWithWorkType(intl, issueKey);
			}
			return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? UPDATE_ITEM_PARENT_FAIL_404_FLAG_WITH_ISSUE_TERM
				: UPDATE_ITEM_PARENT_FAIL_404_FLAG;
		}
		default: {
			return null;
		}
	}
};

export const getUpdateItemParentFail400FlagWithWorkType = (
	intl: IntlShape,
	issueKey?: string | null,
): FlagConfiguration => {
	const updateIssueParent400ErrorDescriptionIssueTerminologyRefreshM4Galaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.updateIssueParent400ErrorDescriptionIssueTerminologyRefreshM4Galaxia
		: messages.updateIssueParent400ErrorDescriptionIssueTerminologyRefreshM4;
	return {
		type: 'error',
		title: intl.formatMessage(messages.updateIssueParent400ErrorTitleIssueTerminologyRefreshM4, {
			issueKey,
		}),
		description: intl.formatMessage(
			updateIssueParent400ErrorDescriptionIssueTerminologyRefreshM4Galaxia,
			{
				issueKey,
			},
		),
		messageId:
			'native-issue-table.services.reparent-mutation.flag.error.update-item-parent-fail-generic-flag.error.flag.update-item-parent-fail-generic.1.1',
		messageType: 'transactional',
	};
};

export const getUpdateItemParentFail404FlagWithWorkType = (
	intl: IntlShape,
	issueKey?: string | null,
): FlagConfiguration => {
	return {
		type: 'error',
		title: intl.formatMessage(messages.updateIssueParent404ErrorTitleIssueTerminologyRefreshM4, {
			issueKey,
		}),
		description: intl.formatMessage(
			messages.updateIssueParent404ErrorDescriptionIssueTerminologyRefreshM4,
			{
				issueKey,
			},
		),
		messageId:
			'native-issue-table.services.reparent-mutation.flag.error.update-item-parent-fail-generic-flag.error.flag.update-item-parent-fail-generic.1.1.1',
		messageType: 'transactional',
	};
};

export const JIRA_ISSUE_TYPENAME = 'JiraIssue';

export const useGetReparentUpdater = () => {
	const { connections } = useConnectionsList();
	const updateConnectionDetails = useUpdateConnectionDetails();

	const getConection = useCallback(
		({ issueId }: { issueId?: string | null }) =>
			Object.values(connections).find((connection) => {
				if (issueId) {
					return connection.type === 'PARENT_CHILDREN' && connection.parentId === issueId;
				}
				return connection.type === 'ROOT_ISSUES';
			}),
		[connections],
	);

	const updater = useCallback(
		({
			store,
			issueId,
			oldParentId,
			newParentId,
			newParentEdgeId,
			index = 0,
			projectKey,
		}: {
			store: RecordSourceProxy;
			issueId: string;
			oldParentId?: string | null;
			newParentId?: string | null;
			newParentEdgeId?: string;
			index?: number;
			projectKey?: string;
		}) => {
			const relayId = RelayDataID({ id: issueId }, JIRA_ISSUE_TYPENAME);
			if (!relayId) {
				return;
			}

			const oldConnection = getConection({ issueId: oldParentId });
			const newConnection = getConection({ issueId: newParentId });

			if (!oldConnection) {
				// Since the issue has to be visible to be repartented, this case should not occur
				return;
			}

			const oldConnectionRecord = store.get(oldConnection.connectionId);
			if (!oldConnectionRecord) {
				return;
			}

			if (newParentEdgeId) {
				updateParentHasChildren({
					store,
					parentIssueEdgeRecordId: newParentEdgeId,
					connectionId: newConnection?.connectionId,
					hasChildren: true,
					projectKey,
				});
			}

			if (newConnection) {
				const newConnectionRecord = store.get(newConnection.connectionId);
				if (!newConnectionRecord) {
					return;
				}
				const existingEdge = oldConnectionRecord
					.getLinkedRecords('edges')
					?.find((edge) => edge?.getLinkedRecord('node')?.getDataID() === relayId);

				// no before or after implies an empty conneciton, verify then create the edges list
				const { EDGES } = ConnectionInterface.get();
				const edges = newConnectionRecord.getLinkedRecords(EDGES);
				if (!edges || !existingEdge) {
					return;
				}
				const nextEdges = edges.slice(0, index).concat(existingEdge).concat(edges.slice(index));

				newConnectionRecord.setLinkedRecords(nextEdges, EDGES);

				updateConnectionDetails({
					store,
					connection: newConnection,
					netChangeInIssueCount: 1,
					remainingIssueCount: newConnectionRecord.getLinkedRecords('edges')?.length,
					projectKey,
				});
			}

			if (oldConnection) {
				// remove the issue from the old connection
				if (
					oldConnectionRecord
						.getLinkedRecords('edges')
						?.some((edge) => edge?.getLinkedRecord('node')?.getDataID() === relayId)
				) {
					ConnectionHandler.deleteNode(oldConnectionRecord, relayId);

					updateConnectionDetails({
						store,
						connection: oldConnection,
						netChangeInIssueCount: -1,
						remainingIssueCount: oldConnectionRecord.getLinkedRecords('edges')?.length,
						projectKey,
					});
				}
			}
		},
		[getConection, updateConnectionDetails],
	);

	return updater;
};
